import { Component, inject, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs';
import { EdiMessageBase, GungEdiAddressInfo, GungEdiExternalId } from '../../../models/edi';
import { EdiService } from '../../../services/edi/edi.service';

interface GungEdiAddressInfoWithData extends GungEdiAddressInfo {
  gln?: number; // This does not exist in the backend but is used in frontend for usability
  title?: string;
  customerId?: string;
  customerName?: string;
  externalId?: GungEdiExternalId;
  erpCustomerId?: string;
  erpCustomerIdLabel?: string;
}
@Component({
    selector: 'lib-edi-gln-number-tabs',
    templateUrl: './edi-gln-number-tabs.component.html',
    styleUrls: ['./edi-gln-number-tabs.component.css'],
    standalone: false
})
export class EdiGlnNumberTabsComponent implements OnInit {
  glnAddressList: GungEdiAddressInfoWithData[] = [];
  @Input()
  ediMessage: EdiMessageBase;

  @Input()
  documentStyle = false;

  constructor(protected ediService: EdiService) {}

  protected translateService = inject(TranslateService);

  // Not every backend translates all EDI party roles (INVOICEE, RECIPIENT, SENDER); show the role readably instead of the key
  partyRole(party: GungEdiAddressInfoWithData): string {
    const translated = this.translateService.instant(party.title);
    if (translated && translated !== party.title) {
      return translated;
    }
    return party.title.charAt(0) + party.title.slice(1).toLowerCase();
  }

  partySummary(party: GungEdiAddressInfoWithData): string {
    const name = [party.customerName, party.name].find(value => value && value !== 'BLANK');
    return [name, party.gln].filter(Boolean).join(' · ');
  }

  private addToSortedList(item: GungEdiAddressInfoWithData): void {
    this.glnAddressList.push(item);
    this.glnAddressList.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }

  protected glnEntries: {
    glnKey: keyof EdiMessageBase;
    externalIdKey: keyof EdiMessageBase;
    addressKey: keyof EdiMessageBase;
    title: string;
    erpCustomerIdKey?: keyof EdiMessageBase;
    erpCustomerIdLabel?: string;
  }[] = [
    { glnKey: 'supplierGln', externalIdKey: 'supplierExternalId', addressKey: 'supplierAddress', title: 'SUPPLIER' },
    { glnKey: 'senderGln', externalIdKey: 'senderExternalId', addressKey: 'senderAddress', title: 'SENDER' },
    { glnKey: 'consigneeGln', externalIdKey: 'consigneeExternalId', addressKey: 'consigneeAddress', title: 'CONSIGNEE' },
    {
      glnKey: 'deliveryGln',
      externalIdKey: 'deliveryExternalId',
      addressKey: 'deliveryAddress',
      title: 'DELIVERY',
      erpCustomerIdKey: 'deliveryCustomerId',
      erpCustomerIdLabel: 'EDI_OVERRIDE_CUSTOMER_ID',
    },
    { glnKey: 'buyerGln', externalIdKey: 'buyerExternalId', addressKey: 'buyerAddress', title: 'BUYER' },
    { glnKey: 'recipientGln', externalIdKey: 'recipientExternalId', addressKey: 'recipientAddress', title: 'RECIPIENT' },
    {
      glnKey: 'invoiceeGln',
      externalIdKey: 'invoiceeExternalId',
      addressKey: 'invoiceeAddress',
      title: 'INVOICEE',
      erpCustomerIdKey: 'billingCustomerId',
      erpCustomerIdLabel: 'EDI_OVERRIDE_BILLING_CUSTOMER_ID',
    },
  ];

  ngOnInit(): void {
    for (const {
      glnKey,
      externalIdKey,
      addressKey,
      title,
      erpCustomerIdKey,
      erpCustomerIdLabel,
    } of this.glnEntries) {
      const gln = this.ediMessage[glnKey] as number;
      const externalId = this.ediMessage[externalIdKey] as GungEdiExternalId;
      const erpCustomerId = erpCustomerIdKey ? (this.ediMessage[erpCustomerIdKey] as string) : undefined;
      if (!gln && !externalId?.id && !erpCustomerId) {
        continue;
      }
      const temp = { ...(this.ediMessage[addressKey] as GungEdiAddressInfoWithData) };
      temp.title = title;
      temp.gln = gln;
      temp.externalId = externalId;
      temp.erpCustomerId = erpCustomerId;
      temp.erpCustomerIdLabel = erpCustomerIdLabel;
      if (!gln) {
        this.addToSortedList(temp);
        continue;
      }
      this.ediService.getGLNRecord(gln).pipe(first()).subscribe(glnRecord => {
        if (!glnRecord) {
          temp.name = 'BLANK';
          temp.customerId = 'BLANK';
          temp.customerName = 'BLANK';
          this.addToSortedList(temp);
          return;
        }
        temp.customerId = glnRecord?.companyId;
        temp.customerName = glnRecord?.b24?.name;
        this.addToSortedList(temp);
      });
    }
  }
}
